/*
 * Reading a CSAT / NPS summary.
 *
 * Kept out of the screen because both of these are judgement calls that would otherwise be
 * invisible inside a render tree, and both are easy to get quietly wrong.
 */

/**
 * The bars of the score histogram.
 *
 * Drawn from the survey's own scale rather than from the scores that happen to be present,
 * so a score nobody ever gives still gets its (empty) bar — "nobody ever rates us 1" is a
 * finding, and a histogram built from the keys in the data hides it.
 *
 * Each bar is measured against the tallest bar rather than against the total: with forty
 * answers on one score and two on another, bars drawn as a share of the total are all
 * invisible.
 */
export const scaleBars = (survey, summary) => {
  if (!survey || !summary) {
    return [];
  }
  const scoreQuestion = (survey.questions || []).find(
    question => question.id === survey.scoreQuestionId
  );
  const scale = scoreQuestion && scoreQuestion.scale;
  if (!scale) {
    return [];
  }

  const counts = summary.histogram || {};
  const highest = Object.keys(counts).reduce(
    (found, key) => Math.max(found, Number(counts[key]) || 0),
    0
  );

  const bars = [];
  for (let score = scale.min; score <= scale.max; score += 1) {
    const count = Number(counts[String(score)]) || 0;
    bars.push({
      score,
      count,
      percent: highest > 0 ? Math.round((count / highest) * 100) : 0
    });
  }
  return bars;
};

/**
 * Whether this summary carries an NPS reading.
 *
 * The backend includes the promoter / detractor fields only for a 0..10 survey, because an
 * NPS is not an average and a 1..5 CSAT has no promoters. The screen asks the summary rather
 * than assuming from the survey code, so a survey renamed in the config still reads right.
 */
export const isNpsSummary = summary => Boolean(summary && typeof summary.nps === "number");

export default scaleBars;
